import React, { useEffect, useMemo, useState } from 'react';
import './_NewBranch.css';
import './_AddProjectUsers.css';
import { ProjectsModel } from '../models/ProjectsModel';
import { ProjectModel, BranchModel, UserModel } from '../models/superbit-api-models';

// React port of the old "add users to project" flow:
//   Project/GetAddUsersView  -> branch/department/user checkbox tree
//   Project/AddUsers         -> saves the selection
// (Views/SystemManagement/Modals/BranchesDepartmentsUsersTree/*).
//
// Selection semantics mirror the old jQuery (branchCheck/userCheck handlers +
// checkUserParents):
//   * checking a branch selects every branch and user in its subtree;
//   * a branch is "checked" once all users in its subtree are checked, and
//     "indeterminate" while only some are;
//   * on save we send only the top-most fully-selected branches and only the
//     individually-selected users (users whose own branch isn't selected), the
//     same minimal payload the old form posted.

type Props = {
    show: boolean;
    project?: ProjectModel;
    onClose: () => void;
    onSaved: () => void;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const isRootParent = (parentId?: string) => !parentId || parentId === EMPTY_GUID;

const AddProjectUsers = (props: Props) => {
    const { show, project, onClose, onSaved } = props;

    const [projectsModel] = useState<ProjectsModel>(() => new ProjectsModel());

    const [branches, setBranches] = useState<BranchModel[]>([]);
    const [users, setUsers] = useState<UserModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');

    const [search, setSearch] = useState<string>('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    // checkedUsers is the source of truth for users; branch "checked" state is
    // derived from it. explicitBranches only holds branches whose subtree has no
    // users (those can't be derived from user state).
    const [checkedUsers, setCheckedUsers] = useState<Set<string>>(new Set());
    const [explicitBranches, setExplicitBranches] = useState<Set<string>>(new Set());

    // ----- Load the tree whenever the modal opens for a project ---------------
    useEffect(() => {
        if (!show || !project?.id) return;
        setSearch('');
        setExpanded(new Set());
        setServerError('');
        load(project.id);
    }, [show, project]);

    const load = async (projectId: string) => {
        setLoading(true);
        try {
            const view = await projectsModel.getAddUsersView(projectId);
            setBranches(view?.branches || []);
            setUsers(view?.users || []);
            // Pre-check users already attached to the project.
            setCheckedUsers(new Set(view?.activeUsers || []));
            setExplicitBranches(new Set());
        } catch (e) {
            console.error('Failed to load add-users view', e);
            setBranches([]);
            setUsers([]);
            setCheckedUsers(new Set());
            setExplicitBranches(new Set());
        } finally {
            setLoading(false);
        }
    };

    // ----- Tree indexes (built once per data change) --------------------------
    const idx = useMemo(() => {
        const childByParent = new Map<string, BranchModel[]>();
        branches.forEach((b) => {
            const key = isRootParent(b.parentId) ? '' : (b.parentId as string);
            if (!childByParent.has(key)) childByParent.set(key, []);
            childByParent.get(key)!.push(b);
        });

        const usersByBranch = new Map<string, UserModel[]>();
        users.forEach((u) => {
            if (!u.branchId) return;
            if (!usersByBranch.has(u.branchId)) usersByBranch.set(u.branchId, []);
            usersByBranch.get(u.branchId)!.push(u);
        });

        const parentOf = new Map<string, string>();
        branches.forEach((b) => {
            if (b.id) parentOf.set(b.id, isRootParent(b.parentId) ? '' : (b.parentId as string));
        });

        const userById = new Map<string, UserModel>();
        users.forEach((u) => {
            if (u.id) userById.set(u.id, u);
        });

        // subtreeUsers[branchId] = every user id anywhere under that branch.
        const subtreeUsers = new Map<string, string[]>();
        const subtreeBranches = new Map<string, string[]>();
        const computeSubtree = (branchId: string): { users: string[]; branchIds: string[] } => {
            if (subtreeUsers.has(branchId)) {
                return { users: subtreeUsers.get(branchId)!, branchIds: subtreeBranches.get(branchId)! };
            }
            const ownUsers = (usersByBranch.get(branchId) || [])
                .map((u) => u.id)
                .filter((id): id is string => !!id);
            const allUsers = [...ownUsers];
            const allBranchIds = [branchId];
            (childByParent.get(branchId) || []).forEach((child) => {
                if (!child.id) return;
                const sub = computeSubtree(child.id);
                allUsers.push(...sub.users);
                allBranchIds.push(...sub.branchIds);
            });
            subtreeUsers.set(branchId, allUsers);
            subtreeBranches.set(branchId, allBranchIds);
            return { users: allUsers, branchIds: allBranchIds };
        };
        branches.forEach((b) => b.id && computeSubtree(b.id));

        const roots = childByParent.get('') || [];
        return { childByParent, usersByBranch, parentOf, userById, subtreeUsers, subtreeBranches, roots };
    }, [branches, users]);

    // A branch is fully selected when its subtree has at least one user and all of
    // them are checked, or (for branches with no users at all) it was checked
    // explicitly.
    const isBranchChecked = (branchId: string): boolean => {
        const subUsers = idx.subtreeUsers.get(branchId) || [];
        if (subUsers.length > 0) return subUsers.every((id) => checkedUsers.has(id));
        return explicitBranches.has(branchId);
    };

    const isBranchIndeterminate = (branchId: string): boolean => {
        if (isBranchChecked(branchId)) return false;
        const subUsers = idx.subtreeUsers.get(branchId) || [];
        if (subUsers.some((id) => checkedUsers.has(id))) return true;
        const subBranches = idx.subtreeBranches.get(branchId) || [];
        return subBranches.some((id) => explicitBranches.has(id));
    };

    // ----- Toggles ------------------------------------------------------------
    const toggleBranch = (branchId: string, checked: boolean) => {
        const subUsers = idx.subtreeUsers.get(branchId) || [];
        const subBranches = idx.subtreeBranches.get(branchId) || [];

        setCheckedUsers((prev) => {
            const next = new Set(prev);
            if (checked) subUsers.forEach((id) => next.add(id));
            else subUsers.forEach((id) => next.delete(id));
            return next;
        });
        // Track branches whose subtree has no users explicitly (can't derive them).
        setExplicitBranches((prev) => {
            const next = new Set(prev);
            subBranches.forEach((id) => {
                const noUsers = (idx.subtreeUsers.get(id) || []).length === 0;
                if (!noUsers) return;
                if (checked) next.add(id);
                else next.delete(id);
            });
            return next;
        });
    };

    const toggleUser = (userId: string, checked: boolean) => {
        setCheckedUsers((prev) => {
            const next = new Set(prev);
            if (checked) next.add(userId);
            else next.delete(userId);
            return next;
        });
    };

    const toggleExpand = (branchId: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(branchId)) next.delete(branchId);
            else next.add(branchId);
            return next;
        });
    };

    // ----- Search visibility --------------------------------------------------
    const term = search.trim().toLowerCase();
    const userMatches = (u: UserModel) => !term || (u.fullName || '').toLowerCase().includes(term);
    const branchVisible = (branchId: string, name?: string): boolean => {
        if (!term) return true;
        if ((name || '').toLowerCase().includes(term)) return true;
        const subUsers = idx.subtreeUsers.get(branchId) || [];
        if (subUsers.some((id) => userMatches(idx.userById.get(id) || ({} as UserModel)))) return true;
        const subBranches = idx.subtreeBranches.get(branchId) || [];
        return subBranches.some((id) =>
            ((branches.find((b) => b.id === id)?.name) || '').toLowerCase().includes(term)
        );
    };

    // ----- Rendering ----------------------------------------------------------
    const renderUserList = (branchId: string): React.ReactNode[] =>
        (idx.usersByBranch.get(branchId) || [])
            .filter(userMatches)
            .map((u) => (
                <li key={`u_${u.id}`} className="addusers-node">
                    <div className="addusers-node__row">
                        <span className="addusers-caret addusers-caret--leaf" />
                        <input
                            type="checkbox"
                            className="checkbox userCheck"
                            checked={!!u.id && checkedUsers.has(u.id)}
                            onChange={(e) => u.id && toggleUser(u.id, e.target.checked)}
                        />
                        <span className="addusers-type-icon addusers-type-icon--user" />
                        <span
                            className="addusers-user-name"
                            onClick={() => u.id && toggleUser(u.id, !checkedUsers.has(u.id))}
                        >
                            {u.fullName}
                        </span>
                    </div>
                </li>
            ));

    const renderBranches = (parentId: string): React.ReactNode[] =>
        (idx.childByParent.get(parentId) || [])
            .filter((b) => b.id && branchVisible(b.id, b.name))
            .map((b) => {
                const branchId = b.id as string;
                const childBranches = idx.childByParent.get(branchId) || [];
                const branchUsers = idx.usersByBranch.get(branchId) || [];
                const expandable = childBranches.length > 0 || branchUsers.length > 0;
                const isOpen = term ? true : expanded.has(branchId);
                const checked = isBranchChecked(branchId);

                return (
                    <li key={`b_${branchId}`} className="addusers-node branchli">
                        <div className="addusers-node__row">
                            {expandable ? (
                                <span
                                    className={`addusers-caret ${isOpen ? 'addusers-caret--open' : ''}`}
                                    onClick={() => toggleExpand(branchId)}
                                />
                            ) : (
                                <span className="addusers-caret addusers-caret--leaf" />
                            )}
                            <input
                                type="checkbox"
                                className="checkbox branchCheck"
                                checked={checked}
                                ref={(el) => {
                                    if (el) el.indeterminate = isBranchIndeterminate(branchId);
                                }}
                                onChange={(e) => toggleBranch(branchId, e.target.checked)}
                            />
                            <span
                                className={`addusers-type-icon ${
                                    parentId === '' ? 'addusers-type-icon--branch' : 'addusers-type-icon--department'
                                }`}
                            />
                            <span
                                className="addusers-branch-name"
                                onClick={() => toggleBranch(branchId, !checked)}
                            >
                                {b.name}
                            </span>
                        </div>
                        {expandable && isOpen && (
                            <ul className="addusers-children">
                                {renderBranches(branchId)}
                                {renderUserList(branchId)}
                            </ul>
                        )}
                    </li>
                );
            });

    // ----- Save ---------------------------------------------------------------
    const buildPayload = (): { users: string[]; branches: string[] } => {
        // A user is sent individually only when its own branch isn't fully selected
        // (a selected branch already implies all its users).
        const userIds = Array.from(checkedUsers).filter((uid) => {
            const u = idx.userById.get(uid);
            const branchId = u?.branchId;
            return !(branchId && isBranchChecked(branchId));
        });

        // Only the top-most fully-selected branch of any selected subtree is sent.
        const branchIds = branches
            .map((b) => b.id)
            .filter((id): id is string => !!id)
            .filter((id) => {
                if (!isBranchChecked(id)) return false;
                const parentId = idx.parentOf.get(id) || '';
                return !parentId || !isBranchChecked(parentId);
            });

        return { users: userIds, branches: branchIds };
    };

    const onSave = async () => {
        if (!project?.id) return;
        setServerError('');
        const payload = buildPayload();
        setSaving(true);
        try {
            await projectsModel.addUsers(project.id, payload.users, payload.branches);
            onSaved();
            onClose();
        } catch (e) {
            console.error('Failed to add users to project', e);
            setServerError('הוספת המשתמשים נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="newbranch-overlay" onClick={handleOverlayClick}>
            <div className="newbranch-modal addusers-modal" dir="rtl">
                <div className="header">
                    <button type="button" className="close" aria-label="Close" onClick={onClose}>
                        <span className="close-modal" />
                    </button>
                    <h3 className="title">הוסף משתמשים{project?.name ? ` - ${project.name}` : ''}</h3>
                </div>

                <div className="addusers-search">
                    <span className="search-icon" />
                    <input
                        type="text"
                        className="form-control addusers-search__input"
                        placeholder="חיפוש"
                        autoComplete="off"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="addusers-legend">
                    <span>
                        <span className="addusers-type-icon addusers-type-icon--branch" />
                        סניף
                    </span>
                    <span>
                        <span className="addusers-type-icon addusers-type-icon--department" />
                        מחלקה
                    </span>
                    <span>
                        <span className="addusers-type-icon addusers-type-icon--user" />
                        משתמש
                    </span>
                </div>

                <div className="addusers-tree">
                    {loading ? (
                        <div className="addusers-empty">טוען…</div>
                    ) : idx.roots.length === 0 ? (
                        <div className="addusers-empty">לא נמצאו סניפים</div>
                    ) : (
                        <ul>{renderBranches('')}</ul>
                    )}
                </div>

                {serverError && <div className="newbranch-server-error text-danger">{serverError}</div>}

                <div className="action-buttons">
                    <div className="action-buttons__half">
                        <button
                            type="button"
                            className="btn btn-link button btn-primary button-outlined"
                            disabled={saving || loading}
                            onClick={onSave}
                        >
                            {saving ? 'שומר…' : 'הוסף'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-link button button-outlined"
                            onClick={onClose}
                        >
                            ביטול
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProjectUsers;
